import React, { useMemo } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChartBar } from '@phosphor-icons/react';
import { ReportFieldMetadata, ReportMeasure } from 'shared/types/asset-report';

const AGGREGATION_LABELS: Record<string, string> = {
  count: 'Aantal',
  sum: 'Som',
  avg: 'Gemiddelde',
  min: 'Minimum',
  max: 'Maximum',
};

interface MeasureSelectorProps {
  fields: ReportFieldMetadata[];
  value: ReportMeasure[];
  onChange: (measures: ReportMeasure[]) => void;
}

const MeasureSelector: React.FC<MeasureSelectorProps> = ({ fields, value, onChange }) => {
  const measureFields = useMemo(() => {
    return fields.filter(f => f.supportsMeasure);
  }, [fields]);

  const isSelected = (field: string, agg: string): boolean => {
    return value.some(m => m.field === field && m.aggregation === agg);
  };

  const toggle = (field: string, agg: string, label: string) => {
    if (isSelected(field, agg)) {
      onChange(value.filter(m => !(m.field === field && m.aggregation === agg)));
    } else {
      onChange([...value, { field, aggregation: agg as any, label: `${AGGREGATION_LABELS[agg]} ${label}`.trim() }]);
    }
  };

  const selectedLabel = value.length === 1
    ? value[0].label
    : value.length > 1
      ? `${value.length} meetwaarden`
      : 'Selecteer meetwaarden';

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">Meetwaarden</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-[200px] justify-between text-sm h-8">
            <span className="truncate">{selectedLabel}</span>
            <ChartBar className="h-3.5 w-3.5 ml-1 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-3" align="start" sideOffset={4}>
          <div className="space-y-3">
            {/* Count is always available */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="__count_count"
                checked={isSelected('__count', 'count')}
                onCheckedChange={() => toggle('__count', 'count', '')}
              />
              <label htmlFor="__count_count" className="text-sm cursor-pointer">Aantal</label>
            </div>

            {measureFields.filter(f => f.field !== '__count').length > 0 && (
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Numerieke velden</p>
                {measureFields.filter(f => f.field !== '__count').map(f => (
                  <div key={f.field} className="mb-2">
                    <p className="text-xs text-muted-foreground mb-1">{f.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['sum', 'avg', 'min', 'max'].map(agg => (
                        <button
                          key={agg}
                          onClick={() => toggle(f.field, agg, f.label)}
                          className={`text-xs px-2 py-0.5 rounded-md border transition-colors ${
                            isSelected(f.field, agg)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-foreground border-border hover:bg-accent'
                          }`}
                        >
                          {AGGREGATION_LABELS[agg]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MeasureSelector;
